import React from 'react';

const COLORS = {
  draft: '#94a3b8',      // slate
  pending: '#f59e0b',    // amber
  approved: '#3b82f6',   // blue
  final: '#6366f1',      // indigo
  sent: '#8b5cf6',       // purple
  won: '#22c55e',        // green
  lost: '#ef4444'        // red
};

/**
 * Funnel chart showing quote distribution by status
 * Uses a horizontal bar visualization for better clarity
 */
const QuoteFunnelChart = ({ data }) => {
  if (!data) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-200/50 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Quote Pipeline Funnel</h3>
        <div className="h-64 flex items-center justify-center text-slate-500">
          No data available
        </div>
      </div>
    );
  }

  // Build status data
  const statuses = [
    { key: 'draft', label: 'Draft', value: data.draftQuotes || 0, color: COLORS.draft, bgColor: 'bg-slate-100' },
    { key: 'pending', label: 'Pending Approval', value: data.pendingQuotes || 0, color: COLORS.pending, bgColor: 'bg-amber-100' },
    { key: 'approved', label: 'Approved', value: data.approvedQuotes || 0, color: COLORS.approved, bgColor: 'bg-blue-100' },
    { key: 'final', label: 'Final', value: data.finalQuotes || 0, color: COLORS.final, bgColor: 'bg-indigo-100' },
    { key: 'sent', label: 'Sent to Customer', value: data.sentQuotes || 0, color: COLORS.sent, bgColor: 'bg-purple-100' },
    { key: 'won', label: 'Won', value: data.acceptedQuotes || 0, color: COLORS.won, bgColor: 'bg-green-100' },
    { key: 'lost', label: 'Lost', value: data.lostQuotes || 0, color: COLORS.lost, bgColor: 'bg-red-100' },
  ];

  const totalQuotes = data.totalQuotes || 1; // Avoid division by zero

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-200/50 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-slate-800">Quote Pipeline Funnel</h3>
        <div className="text-sm text-slate-500">
          Total: <span className="font-semibold text-slate-800">{data.totalQuotes || 0}</span> quotes
        </div>
      </div>

      {/* Scale Header */}
      <div className="flex items-center gap-4 mb-2">
        <div className="w-36 flex-shrink-0" />
        <div className="flex-1 relative">
          <div className="flex justify-between text-xs text-slate-400">
            <span>0%</span>
            <span>50%</span>
            <span>100%</span>
          </div>
        </div>
        <div className="w-16" />
      </div>

      {/* Funnel Visualization */}
      <div className="space-y-3">
        {statuses.map((status) => {
          const percentage = totalQuotes > 0 ? ((status.value / totalQuotes) * 100).toFixed(1) : 0;
          const barWidth = (status.value / totalQuotes) * 100;

          return (
            <div key={status.key} className="group">
              <div className="flex items-center gap-4">
                {/* Label */}
                <div className="w-36 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: status.color }}
                    />
                    <span className="text-sm font-medium text-slate-700">{status.label}</span>
                  </div>
                </div>

                {/* Bar with gridlines */}
                <div className="flex-1 h-8 bg-slate-100/50 rounded-lg overflow-hidden relative">
                  {/* Gridlines */}
                  <div className="absolute inset-0 flex">
                    <div className="w-1/2 border-r border-slate-300/50" />
                    <div className="w-1/2" />
                  </div>
                  {/* Bar */}
                  <div
                    className="h-full rounded-lg transition-all duration-500 ease-out flex items-center justify-end relative z-10"
                    style={{
                      width: `${barWidth}%`,
                      minWidth: status.value > 0 ? '24px' : '0',
                      backgroundColor: status.color,
                      opacity: 0.9
                    }}
                  >
                    {status.value > 0 && (
                      <span className="text-white text-sm font-bold px-2">
                        {status.value}
                      </span>
                    )}
                  </div>
                </div>

                {/* Percentage */}
                <div className="w-16 text-right">
                  <span className="text-sm font-medium text-slate-500">{percentage}%</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Stats */}
      <div className="mt-6 pt-4 border-t border-slate-200/50 grid grid-cols-3 gap-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{data.pipelineQuotes || 0}</div>
          <div className="text-xs text-slate-500 mt-1">In Pipeline</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{data.acceptedQuotes || 0}</div>
          <div className="text-xs text-slate-500 mt-1">Won</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-slate-600">
            {(data.totalQuotes || 0) > 0 ? Math.round(((data.acceptedQuotes || 0) / (data.totalQuotes || 1)) * 100) : 0}%
          </div>
          <div className="text-xs text-slate-500 mt-1">Conversion</div>
        </div>
      </div>
    </div>
  );
};

export default QuoteFunnelChart;
